/* W8.2 → W9.1 — Decision Journal (Platform) : journal, Sensitivity Lab (W8.7),
 * comparaison décomposable (W8.8), Outcome Learning (W8.9), mémoire organisationnelle (W9.1).
 * Snapshots historiques immuables. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { Scale3d, Loader2, TrendingUp } from "lucide-react";
import { DecisionLab } from "@/components/decisions/DecisionLab";
import { DecisionCompare } from "@/components/decisions/DecisionCompare";
import { OutcomeLearning } from "@/components/decisions/OutcomeLearning";
import { DecisionMemory } from "@/components/decisions/DecisionMemory";
import { ManualFmDecisionForm } from "@/components/ManualFmDecision";
import { ExportButton } from "@/components/ExportButton";
import { usePersona } from "@/lib/persona";

/* W17-B5 — Exposition de la complexité : l'essence d'abord (recommandation ·
   confiance · EV · principal risque), toute la profondeur en drill-down.
   Même moteur, même donnée — profondeur cognitive différente. */
function DecisionEssence({ d, children }) {
  const { manifest } = usePersona();
  const [showFull, setShowFull] = useState(!!manifest?.show_raw_data);
  const eco = d.economics || {};
  const cur = (d.context || {}).currency || "CHF";
  const conf = eco.confidence_avg;
  const confLabel = conf == null ? "—" : conf >= 0.7 ? "élevée" : conf >= 0.5 ? "moyenne" : "faible";
  const verdict = d.decision?.verdict || d.recommendation;
  const topNeg = (eco.factors || []).find((f) => f.contribution_chf < 0);
  const money = (v) => v == null ? "—" : new Intl.NumberFormat("fr-CH", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(v);
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <div className="mt-2 p-3 rounded border border-[#7F77DD]/25 bg-[#7F77DD]/5 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid={`decision-essence-${d.id}`}>
        <span className="text-sm font-semibold text-white">{d.decision ? "" : "NOAM recommande : "}{verdict || "—"}</span>
        <span className="text-[11px] text-zinc-300">Confiance : <b>{confLabel}</b></span>
        <span className="text-[11px] text-zinc-300">Valeur attendue : <b>{money(eco.adjusted_ev_chf)}</b></span>
        {topNeg && <span className="text-[11px] text-zinc-300">Principal risque : <b>{topNeg.scenario}</b> ({money(topNeg.contribution_chf)})</span>}
        <button onClick={() => setShowFull((v) => !v)} data-testid={`decision-essence-drill-${d.id}`}
          className="ml-auto text-[10px] text-[#7F77DD] hover:underline">
          {showFull ? "Réduire" : "Comprendre — scénarios, hypothèses, sensibilité, provenance →"}
        </button>
      </div>
      {showFull && children}
    </div>
  );
}

const fmtMoney = (v, cur = "CHF") => v == null ? "—" : new Intl.NumberFormat("fr-CH", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(v);
const fmtCHF = (v) => fmtMoney(v, "CHF");
const VERDICT_STYLE = { PURSUE: "border-[#1D9E75]/50 text-[#1D9E75]", HOLD: "border-[#EF9F27]/50 text-[#EF9F27]", PASS: "border-[#E24B4A]/50 text-[#E24B4A]" };
const STATUS_STYLE = { draft: "text-zinc-400 border-white/15", decided: "text-[#378ADD] border-[#378ADD]/40", closed: "text-[#1D9E75] border-[#1D9E75]/40" };
const TABS = [
  { key: "journal", label: "Journal" },
  { key: "lab", label: "Sensitivity Lab" },
  { key: "compare", label: "Comparaison" },
  { key: "learning", label: "Apprentissage" },
  { key: "memory", label: "Mémoire" },
];
const SYSTEMS = ["", "deal", "financial_markets", "governance", "platform"];

function DecisionDetail({ d }) {
  const cur = (d.context || {}).currency || "CHF";
  const snapEco = ((d.decision || {}).snapshot || {}).economics;
  const eco = d.economics || {};
  const v = d.estimation_version || 1;
  return (
    <div className="mt-2 pt-2 border-t border-white/5 space-y-2 text-[11px]" data-testid={`journal-detail-${d.id}`}>
      {d.question && <div className="text-zinc-400">{d.question}</div>}
      {(d.decision || {}).rationale && (
        <div className="text-zinc-300 italic">Pourquoi : « {d.decision.rationale} » — {d.decision.decided_by_email}</div>
      )}
      <div className="space-y-0.5">
        {(d.scenarios || []).map((s) => (
          <div key={s.id} className="flex gap-2 text-[10px] text-zinc-400 flex-wrap">
            <span className="w-40 truncate">{s.label}</span>
            <span className="font-mono text-[#7F77DD]">{Math.round(s.probability.value * 100)}%</span>
            <span className={s.payoff_chf >= 0 ? "text-[#1D9E75]" : "text-[#E24B4A]"}>{fmtMoney(s.payoff_chf, cur)}</span>
            <span className="text-zinc-600 truncate max-w-[280px]">source : {s.probability.source} ({s.probability.method}, conf. {Math.round((s.probability.confidence || 0) * 100)}%)</span>
          </div>
        ))}
      </div>
      <div className="flex gap-4 text-[10px] text-zinc-400 flex-wrap">
        {snapEco && <span>AdjEV à la décision (snapshot) <b className="text-zinc-200">{fmtMoney(snapEco.adjusted_ev_chf, cur)}</b></span>}
        <span>Conviction actuelle (v{v}) <b className="text-zinc-200">{fmtMoney(eco.adjusted_ev_chf, cur)}</b></span>
        {eco.breakeven_p_success != null && <span>Break-even P(succès) <b className="text-zinc-200">{Math.round(eco.breakeven_p_success * 100)}%</b></span>}
        {eco.downside_chf != null && <span>Downside <b className="text-[#E24B4A]">{fmtMoney(eco.downside_chf, cur)}</b></span>}
        {d.outcome && <span>Réalisé <b className={d.outcome.payoff_realized_chf >= 0 ? "text-[#1D9E75]" : "text-[#E24B4A]"}>{fmtMoney(d.outcome.payoff_realized_chf, cur)}</b></span>}
      </div>
      {snapEco && v > 1 && (
        <div className="text-[10px] text-zinc-500">Le snapshot de décision (v1) est immuable — la conviction v{v} est la dernière ré-estimation journalisée.</div>
      )}
    </div>
  );
}

function LifecycleActions({ d, onReload }) {
  const [verdict, setVerdict] = useState("PURSUE");
  const [rationale, setRationale] = useState("");
  const [scenarioId, setScenarioId] = useState("");
  const [payoff, setPayoff] = useState("");
  const [notes, setNotes] = useState("");
  const decide = async () => {
    if (!rationale.trim()) return notify.error("Justification requise pour décider.");
    try {
      await api.post(`/workspace/decisions/${d.id}/decide`, { verdict, rationale });
      notify.success(`Décision figée : ${verdict} (snapshot immuable).`); onReload();
    } catch (e) { notify.error(fmtError(e)); }
  };
  const recordOutcome = async () => {
    try {
      await api.post(`/workspace/decisions/${d.id}/outcome`, {
        scenario_id_realized: scenarioId || null,
        payoff_realized_chf: payoff === "" ? null : Number(payoff),
        notes,
      });
      notify.success("Outcome enregistré — décision clôturée, la calibration apprend."); onReload();
    } catch (e) { notify.error(fmtError(e)); }
  };
  if (d.status === "closed") return null;
  return (
    <div className="mt-2 pt-2 border-t border-white/5 flex flex-wrap items-center gap-2"
      onClick={(e) => e.stopPropagation()} data-testid={`journal-lifecycle-${d.id}`}>
      {d.status === "draft" && (
        <>
          <select value={verdict} onChange={(e) => setVerdict(e.target.value)}
            className="noam-input text-[10px] w-24" data-testid={`journal-verdict-${d.id}`}>
            {["PURSUE", "HOLD", "PASS"].map((v) => <option key={v}>{v}</option>)}
          </select>
          <input value={rationale} onChange={(e) => setRationale(e.target.value)}
            placeholder="Justification (obligatoire)" className="noam-input text-[10px] flex-1 min-w-[180px]"
            data-testid={`journal-rationale-${d.id}`} />
          <button onClick={decide} className="btn-primary text-[10px]" data-testid={`journal-decide-${d.id}`}>Décider</button>
        </>
      )}
      {d.status === "decided" && (
        <>
          <select value={scenarioId} onChange={(e) => setScenarioId(e.target.value)}
            className="noam-input text-[10px] w-44" data-testid={`journal-outcome-scenario-${d.id}`}>
            <option value="">Scénario réalisé (optionnel)</option>
            {(d.scenarios || []).map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <input type="number" value={payoff} onChange={(e) => setPayoff(e.target.value)}
            placeholder="Payoff réalisé CHF" className="noam-input text-[10px] w-36"
            data-testid={`journal-outcome-payoff-${d.id}`} />
          <input value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes" className="noam-input text-[10px] flex-1 min-w-[120px]"
            data-testid={`journal-outcome-notes-${d.id}`} />
          <button onClick={recordOutcome} className="btn-primary text-[10px]"
            data-testid={`journal-outcome-${d.id}`}>Outcome & clôturer</button>
        </>
      )}
    </div>
  );
}

function JournalTab() {
  const [items, setItems] = useState(null);
  const [cal, setCal] = useState(null);
  const [filter, setFilter] = useState("");
  const [system, setSystem] = useState(() => new URLSearchParams(window.location.search).get("system") || "");
  const [openId, setOpenId] = useState(null);
  const [showFmForm, setShowFmForm] = useState(false);

  const loadItems = () => {
    const p = system ? `?system=${system}` : "";
    api.get(`/workspace/decisions${p}`).then((r) => setItems(r.data.items)).catch((e) => notify.error(fmtError(e)));
    api.get("/workspace/decisions/calibration").then((r) => setCal(r.data)).catch(() => {});
  };
  useEffect(() => { loadItems(); }, [system]); // eslint-disable-line

  if (!items) return <div className="flex items-center justify-center py-20 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  const shown = filter ? items.filter((d) => d.status === filter) : items;

  return (
    <div className="space-y-4">
      {cal && (
        <div className="noam-card p-4" data-testid="decision-calibration">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5 mb-2">
            <TrendingUp className="w-3.5 h-3.5 text-[#7F77DD]" /> Calibration
          </div>
          <div className="flex gap-4 text-xs text-zinc-400 flex-wrap">
            <span>Décisions <b className="text-zinc-200">{cal.decisions_total}</b></span>
            <span>Ouvertes <b className="text-zinc-200">{cal.by_status?.draft ?? 0}</b></span>
            <span>Décidées <b className="text-zinc-200">{cal.by_status?.decided ?? 0}</b></span>
            <span>Résolues <b className="text-zinc-200">{cal.by_status?.closed ?? 0}</b></span>
            {cal.status === "INSUFFICIENT_DATA" ? (
              <span className="px-2 py-0.5 rounded border border-[#EF9F27]/40 text-[#EF9F27]" data-testid="calibration-insufficient">
                INSUFFICIENT_DATA — {cal.scored_outcomes}/{cal.min_sample} outcomes scorés : la calibration s'activera avec l'historique
              </span>
            ) : (
              <>
                <span>Brier <b className="text-zinc-200">{cal.brier_mean}</b></span>
                <span>Hit rate <b className="text-zinc-200">{Math.round((cal.top_scenario_hit_rate || 0) * 100)}%</b></span>
                {cal.bias_reading && <span>Biais <b className="text-zinc-200">{cal.bias_reading} ({cal.bias > 0 ? "+" : ""}{cal.bias})</b></span>}
              </>
            )}
          </div>
        </div>
      )}

      <div className="flex gap-1.5 items-center flex-wrap">
        {["", "draft", "decided", "closed"].map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={`text-[10px] px-2.5 py-1 rounded-full border transition-colors ${filter === s ? "border-[#7F77DD]/60 text-[#7F77DD] bg-[#7F77DD]/10" : "border-white/10 text-zinc-500"}`}
            data-testid={`journal-filter-${s || "all"}`}>
            {s === "" ? "Toutes" : s === "draft" ? "Ouvertes" : s === "decided" ? "Décidées" : "Résolues"}
          </button>
        ))}
        <select value={system} onChange={(e) => setSystem(e.target.value)} className="noam-input text-[10px] w-40 ml-auto" data-testid="journal-system-filter">
          {SYSTEMS.map((s) => <option key={s} value={s}>{s || "Tous les systems"}</option>)}
        </select>
        <button onClick={() => setShowFmForm(!showFmForm)} className="btn-ghost text-[10px] border border-[#43C6D8]/40 text-[#43C6D8]" data-testid="journal-fm-manual-toggle">
          + Décision manuelle FM
        </button>
        <ExportButton system="financial_markets" label="Exporter FM" />
        <Link to="/app/board-pack" className="btn-ghost text-[10px] border border-[#378ADD]/40 text-[#378ADD]" data-testid="journal-board-pack-link">Dossier Comité</Link>
      </div>
      {showFmForm && <ManualFmDecisionForm onDone={() => { setShowFmForm(false); loadItems(); }} />}

      {shown.length === 0 && (
        <div className="p-10 text-center text-xs text-zinc-600 border border-dashed border-white/10 rounded-lg" data-testid="journal-empty">
          Aucune décision journalisée. Créez-en depuis un Deal 360 (onglet Décisions).
        </div>
      )}
      <div className="space-y-2">
        {shown.map((d) => {
          const eco = d.economics || {};
          const cur = (d.context || {}).currency || "CHF";
          const dealRef = (d.context || {}).ref_type === "deal" ? d.context.ref_id : null;
          const open = openId === d.id;
          return (
            <div key={d.id} className="noam-card p-3 hover:bg-white/[0.03] transition-colors cursor-pointer"
              onClick={() => setOpenId(open ? null : d.id)} data-testid={`journal-row-${d.id}`}>
              <div className="flex items-center gap-3 text-xs">
                <Scale3d className="w-3.5 h-3.5 text-[#7F77DD] shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-zinc-200 truncate">{d.title}</div>
                  <div className="text-[10px] text-zinc-500">
                    {(d.context || {}).system || "platform"} · {new Date(d.created_at).toLocaleDateString("fr-CH")}
                    {(d.estimation_version || 1) > 1 && <span className="text-[#7F77DD]"> · conviction v{d.estimation_version}</span>}
                    {d.decision?.decided_by_email && ` · décidé par ${d.decision.decided_by_email}`}
                  </div>
                </div>
                <span className="hidden sm:block text-[10px] text-zinc-400">AdjEV v{d.estimation_version || 1} <b className="text-zinc-200">{fmtMoney(eco.adjusted_ev_chf, cur)}</b></span>
                <span className="hidden sm:block text-[10px] text-zinc-400">Conf. <b className="text-zinc-200">{eco.confidence_avg != null ? Math.round(eco.confidence_avg * 100) + "%" : "—"}</b></span>
                {d.decision && <span className={`text-[9px] px-1.5 py-0.5 rounded-full border shrink-0 ${VERDICT_STYLE[d.decision.verdict]}`}>{d.decision.verdict}</span>}
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full border shrink-0 ${STATUS_STYLE[d.status]}`}>{d.status}</span>
                {d.status !== "closed" && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-amber-400/40 text-amber-400 shrink-0"
                    data-testid={`journal-pending-${d.id}`}>
                    {d.status === "draft" ? "à décider" : "outcome attendu"}
                  </span>
                )}
              </div>
              {open && <DecisionEssence d={d}><DecisionDetail d={d} /></DecisionEssence>}
              {open && <LifecycleActions d={d} onReload={loadItems} />}
              {open && dealRef && (
                <Link to={`/app/deals/${dealRef}`} onClick={(e) => e.stopPropagation()}
                  className="inline-block mt-2 text-[10px] text-[#378ADD] hover:underline" data-testid={`journal-open-deal-${d.id}`}>
                  Ouvrir le deal lié →
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function DecisionJournal() {
  const [tab, setTab] = useState("journal");
  return (
    <div className="space-y-5" data-testid="decision-journal-root">
      <div>
        <div className="noam-overline">NOAM Platform</div>
        <h1 className="noam-display text-2xl font-bold tracking-tight">Decision Intelligence</h1>
        <p className="text-xs text-zinc-500 mt-1">Ce que l'organisation savait → estimait → a décidé → ce qui s'est produit. Snapshots immuables.</p>
      </div>
      <div className="flex gap-1.5 flex-wrap border-b border-white/5 pb-2">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`text-[11px] px-3 py-1.5 rounded-full border transition-colors ${tab === t.key ? "border-[#7F77DD]/60 text-[#7F77DD] bg-[#7F77DD]/10" : "border-white/10 text-zinc-500 hover:text-zinc-300"}`}
            data-testid={`decision-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === "journal" && <JournalTab />}
      {tab === "lab" && <DecisionLab />}
      {tab === "compare" && <DecisionCompare />}
      {tab === "learning" && <OutcomeLearning />}
      {tab === "memory" && <DecisionMemory />}
    </div>
  );
}
